import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { ytEmbed } from "@/lib/api";
import { Loader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";

function Countdown({ date, time }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const target = new Date(`${date}T17:00:00`).getTime();
    const t = setInterval(() => {
      const diff = target - Date.now();
      if (diff <= 0) { setLeft("LIVE NOW"); return; }
      const h = Math.floor(diff / 3.6e6), m = Math.floor((diff % 3.6e6) / 6e4), s = Math.floor((diff % 6e4) / 1000);
      setLeft(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    }, 1000);
    return () => clearInterval(t);
  }, [date, time]);
  return <span className="font-head text-3xl tabular-nums">{left}</span>;
}

export default function SessionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: s, isLoading, refetch } = useQuery({ queryKey: ["session", id], queryFn: async () => (await api.get(`/sessions/${id}`)).data });
  const [watching, setWatching] = useState(false);

  if (isLoading || !s) return <Loader />;
  const hasReplay = !!s.replay_url;
  const embed = ytEmbed(s.replay_url);

  const startReplay = async () => {
    setWatching(true);
    if (s.my_replay_status === "not_watched") {
      const r = await api.post("/replays/progress", { session_id: id, status: "started" });
      if (r.data.points_awarded) toast.success(`+${r.data.points_awarded} XP for watching!`);
      refetch();
    }
  };
  const completeReplay = async () => {
    const r = await api.post("/replays/progress", { session_id: id, status: "completed" });
    if (r.data.points_awarded) toast.success(`🎬 Replay complete! +${r.data.points_awarded} XP`);
    refetch();
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="session-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-bold text-csh-purple"><Icons.ChevronLeft size={16} /> Back</button>

      <div className="rounded-3xl gradient-blue p-6 text-white">
        <div className="text-sm font-bold uppercase text-white/60">Week {s.week_number} · Session {s.session_number}</div>
        <h1 className="font-head text-3xl">{s.title}</h1>
        <p className="mt-1 text-white/80">{s.description}</p>
        <div className="mt-4 flex flex-wrap gap-3 text-sm font-bold">
          <span className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5"><Icons.CalendarDays size={15} /> {s.date}</span>
          <span className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5"><Icons.Clock size={15} /> {s.time}</span>
        </div>
      </div>

      {/* Countdown / Join */}
      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5 text-center">
        <p className="text-sm font-bold uppercase text-muted-foreground">Class starts in</p>
        <div className="my-2 text-csh-orange"><Countdown date={s.date} time={s.time} /></div>
        {s.meeting_link && (
          <a href={s.meeting_link} target="_blank" rel="noreferrer" data-testid="join-live-btn">
            <Button className="rounded-full gradient-orange px-8 py-6 font-head text-lg uppercase text-white hover:scale-105">
              <Icons.Video size={20} /> Join Live Class
            </Button>
          </a>
        )}
      </div>

      {/* Replay */}
      <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-head text-xl text-csh-blue">🎬 Watch Replay</h3>
          <span className={`rounded-full px-3 py-1 text-xs font-bold ${
            s.my_replay_status === "completed" ? "bg-green-100 text-green-700" :
            s.my_replay_status === "started" ? "bg-yellow-100 text-yellow-700" : "bg-black/10 text-csh-grey/60"}`}>
            {s.my_replay_status === "completed" ? "🟢 Completed" : s.my_replay_status === "started" ? "🟡 Started" : "🔴 Not Watched"}
          </span>
        </div>
        {!hasReplay ? (
          <div className="rounded-2xl bg-csh-blue/5 py-10 text-center text-muted-foreground">The replay is coming soon! 🎬</div>
        ) : !watching ? (
          <button onClick={startReplay} data-testid="watch-replay-btn"
            className="flex aspect-video w-full items-center justify-center rounded-2xl gradient-purple text-white hover:opacity-90">
            <Icons.PlayCircle size={64} />
          </button>
        ) : (
          <div className="space-y-3">
            <div className="aspect-video overflow-hidden rounded-2xl">
              <iframe src={embed} title="replay" className="h-full w-full" allowFullScreen />
            </div>
            {s.my_replay_status !== "completed" && (
              <Button onClick={completeReplay} data-testid="complete-replay-btn"
                className="w-full rounded-full gradient-orange py-5 font-bold text-white">
                <Icons.CheckCircle2 size={18} /> Mark Replay as Watched (+XP)
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Takeaways */}
      {s.takeaways?.length > 0 && (
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
          <h3 className="font-head text-xl text-csh-blue">✨ Session Takeaways</h3>
          <ul className="mt-3 space-y-2">
            {s.takeaways.map((t, i) => (
              <li key={i} className="flex items-center gap-2 text-csh-grey"><Icons.CheckCircle2 size={18} className="text-green-500 shrink-0" /> {t}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Quests unlocked */}
      {s.quests?.length > 0 && (
        <div className="rounded-3xl bg-white p-6 shadow-sm border border-black/5">
          <h3 className="font-head text-xl text-csh-blue">🎯 Quests Unlocked</h3>
          <div className="mt-3 space-y-2">
            {s.quests.map((q) => (
              <button key={q.id} onClick={() => navigate(`/app/quests/${q.id}`)}
                className="flex w-full items-center justify-between rounded-2xl bg-csh-orange/10 px-4 py-3 hover:bg-csh-orange/20">
                <span className="font-bold text-csh-blue">Quest #{q.number} — {q.name}</span>
                <Icons.ArrowRight size={18} className="text-csh-orange" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
